import { Formik } from 'formik';

import { isMobile } from 'hooks';
import { ButtonColors } from 'types';
import { Icon } from 'components/icon';
import * as CommonStyles from 'components/styles';
import { Dialog, DialogType } from 'components/dialog';
import { TimeOffDecision } from 'store/api/time-off/types';
import { Button, ButtonTypes, ButtonWithRef } from 'components/button';
import { InputField, SelectField, DatePickerField } from 'components/fields';
import { laterDay, dayToDate, longRange, newYorkToday } from 'utils/time-off';

import { TimeOffDecisionDialogProps } from './types';
import {
  DaysValue,
  isSomeDays,
  pickedDays,
  approvedRange,
  decisionOptions,
} from './config';

// TIME OFF (owner 24 Oct 2026; plan §0.12, §6.2): change an Approved
// request before its first day; the rep gets a text with the new decision
// when they have a phone on file.
interface Values {
  days: DaysValue;
  declineNote: string;
  // Separate fields: a some-days note never becomes a decline reason.
  someDaysNote: string;
  decision: TimeOffDecision;
}

export const EditDecisionDialog = ({
  row,
  decide,
}: TimeOffDecisionDialogProps) => {
  const mobile = isMobile();
  const allDaysPast = row.isPartial && row.startDate < newYorkToday();
  const current: TimeOffDecision = row.isPartial
    ? 'approvedSomeDays'
    : 'approved';
  const initialValues: Values = {
    declineNote: '',
    decision: current,
    days: approvedRange(row),
    someDaysNote: row.decisionNote ?? '',
  };

  const changed = (values: Values) => {
    if (values.decision !== current) {
      return true;
    }
    if (values.decision !== 'approvedSomeDays') {
      return false;
    }
    const picked = pickedDays(values.days);
    return (
      picked.startDate !== row.approvedStartDate ||
      picked.endDate !== row.approvedEndDate ||
      values.someDaysNote.trim() !== (row.decisionNote ?? '')
    );
  };

  const isValid = (values: Values) => {
    if (values.decision === 'notApproved') {
      const note = values.declineNote.trim();
      return note.length >= 5 && note.length <= 200;
    }
    if (values.decision === 'approvedSomeDays') {
      return isSomeDays(row, values.days);
    }
    return true;
  };

  return (
    <Dialog
      title="Edit decision"
      closeOnDocumentClick={false}
      trigger={
        <ButtonWithRef
          fullWidth={mobile}
          icon={<Icon.Edit />}
          tooltip="Edit decision"
          colorType={ButtonColors.LightBlue}
        />
      }
    >
      {(close, _, ButtonWrap) => (
        <Formik
          initialValues={initialValues}
          onSubmit={async (values) => {
            const picked = pickedDays(values.days);
            const result = await decide(
              {
                decision: values.decision,
                note:
                  (values.decision === 'notApproved'
                    ? values.declineNote
                    : values.someDaysNote
                  ).trim() || null,
                ...(values.decision === 'approvedSomeDays'
                  ? {
                      approvedEndDate: picked.endDate,
                      approvedStartDate: picked.startDate,
                    }
                  : {}),
              },
              'edit'
            );
            if (result) {
              close();
            }
          }}
        >
          {({ values, isSubmitting }) => (
            <CommonStyles.Form gap={24}>
              <p>
                {row.name} asked for{' '}
                <span className="semibold">
                  {longRange(row.startDate, row.endDate, { dropYear: true })}
                </span>
                . The new decision replaces the current one
                {row.hasPhone ? `, and ${row.name} gets a text.` : '.'}
              </p>

              <SelectField
                onlyValue
                defaultControl
                name="decision"
                label="Decision"
                isSearchable={false}
                menuPlacement="auto"
                options={decisionOptions.filter(
                  ({ value }) =>
                    (value !== 'approvedSomeDays' || row.days >= 2) &&
                    // Back to all the days would start in the past.
                    (value !== 'approved' || !allDaysPast)
                )}
              />

              {values.decision === 'approvedSomeDays' && (
                <>
                  <DatePickerField
                    name="days"
                    selectsRange
                    // The pop-up focuses this field on open; keep the calendar shut
                    // until it is clicked, so it doesn't cover the form (Dev pass 24 Oct).
                    preventOpenOnFocus
                    label="Approved days"
                    placeholderText="Approved days"
                    maxDate={dayToDate(row.endDate) ?? undefined}
                    minDate={
                      dayToDate(laterDay(row.startDate, newYorkToday())) ??
                      undefined
                    }
                  />
                  <InputField
                    type="textarea"
                    maxLength={200}
                    name="someDaysNote"
                    label="Note to the rep (optional)"
                    placeholder="e.g. Need you back for the 14th"
                  />
                </>
              )}

              {values.decision === 'notApproved' && (
                <>
                  <InputField
                    label="Reason"
                    type="textarea"
                    name="declineNote"
                    placeholder="Add a reason"
                  />
                  <p>Required, 5–200 characters. The rep sees this.</p>
                </>
              )}

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  fullWidth
                  text="Cancel"
                  onClick={close}
                  colorType={ButtonColors.LightBlue}
                />
                <Button
                  fullWidth
                  text="Save"
                  type={ButtonTypes.Submit}
                  colorType={ButtonColors.LightGreen}
                  disabled={
                    !changed(values) || !isValid(values) || isSubmitting
                  }
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
